/**
 * Milo's 3D head — the part that is about the browser rather than about a component.
 *
 * # What is served and what is bundled
 *
 * Since v1.456.0 the two are cleanly separated, which they were not before.
 *
 * **Served** (`public/milo/<version>/`): the model and its poster, and nothing that runs.
 * `MILO-Head-EyeMorphs.glb` is a megabyte of geometry, ten clips and twelve eye morph
 * targets; it is a file, not a module, and it is fetched by the renderer at run time.
 *
 * **Bundled**: the renderer. `three` is an npm dependency (deviation 29 in `CLAUDE.md`)
 * and `libs/miloRenderer.ts` is imported by `MiloHead.vue` with a dynamic `import()`, so
 * Vite puts the whole of it in its OWN asynchronous chunk: the login page does not carry
 * it, and an account without `chat:use` never downloads it. That was the property the
 * vendored `<model-viewer>` package bought by staying out of the build entirely, and it
 * survives the move — a dynamic import buys the same thing without a vendored megabyte
 * that TypeScript cannot read.
 *
 * # Why the folder is versioned
 *
 * Caddy serves an existing file under `/opt/dist` without a `Cache-Control` header
 * (deviation 13 puts the no-store headers on the SPA fallback, which is right), so a
 * browser is free to keep `MILO-Head-EyeMorphs.glb` for as long as it likes. A model and
 * the code that drives it must agree — the eye morph names, the clip names — and a new
 * version is a new folder plus the one line below, so an old model can never be handed to
 * a new renderer. `1.1.0` stays on disk for this release as the way back.
 */

/** The folder the model is served from. See the note above before changing it. */
export const MILO_ASSET_BASE = `${import.meta.env.BASE_URL}milo/2.0.0`

/** Asked once per page; the answer cannot change while the tab is open. */
let webglSupported: boolean | undefined

/**
 * Whether this browser can draw the head at all.
 *
 * Asked BEFORE the dynamic import rather than after it, so a machine without WebGL — a
 * locked down terminal, a virtual desktop with no GPU passthrough, a browser with the
 * flag off — draws the CSS orb instantly instead of downloading a renderer and a model in
 * order to fail. The canvas is thrown away immediately; the question is only whether a
 * context exists.
 */
export const miloRendererSupported = (): boolean => {
    if (webglSupported !== undefined) return webglSupported

    try {
        const canvas = document.createElement('canvas')

        webglSupported = !!(canvas.getContext('webgl2') ?? canvas.getContext('webgl'))
    } catch {
        webglSupported = false
    }

    return webglSupported
}
